import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/landing/Navbar";
import { ChevronRight, ArrowLeft } from "lucide-react";

const TOC = [
  { id: "abstract", label: "Abstract" },
  { id: "exec", label: "1. Executive Summary" },
  { id: "intro", label: "2. Introduction" },
  { id: "market", label: "3. Market Analysis" },
  { id: "problem", label: "4. Problem Statement" },
  { id: "solution", label: "5. The Qovarix Solution" },
  { id: "arch", label: "6. Technical Architecture" },
  { id: "contracts", label: "7. Smart Contract Design" },
  { id: "ai", label: "8. AI Sentiment Engine" },
  { id: "tokenomics", label: "9. Tokenomics" },
  { id: "economic", label: "10. Economic Model" },
  { id: "security", label: "11. Security & Audits" },
  { id: "governance", label: "12. Governance" },
  { id: "roadmap", label: "13. Roadmap" },
  { id: "team", label: "14. Team" },
  { id: "risk", label: "15. Risk Disclosure" },
  { id: "legal", label: "16. Legal & Compliance" },
  { id: "conclusion", label: "17. Conclusion" },
];

function Sec({ id, title, children }) {
  return (
    <section id={id} className="mb-16 scroll-mt-24">
      <h2 className="font-grotesk text-2xl sm:text-3xl font-bold text-white mb-6 tracking-tight">{title}</h2>
      <div className="space-y-4 font-grotesk text-[15px] text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mb-6">
      <h3 className="font-grotesk text-lg font-semibold text-white mb-3">{title}</h3>
      <div className="space-y-3 text-slate-300">{children}</div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2">
      <ChevronRight size={12} className="text-[#00F0FF] mt-1.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function DataRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5">
      <span className="font-mono text-xs text-slate-500 uppercase tracking-wider">{label}</span>
      <span className="font-mono text-sm text-white">{value}</span>
    </div>
  );
}

export default function WhitepaperPage() {
  const [active, setActive] = useState("abstract");

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }); },
      { rootMargin: "-20% 0px -60% 0px" }
    );
    TOC.forEach(({ id }) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#030406" }}>
      <Navbar />

      {/* Hero Banner */}
      <div className="pt-24 pb-12 px-6 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="font-mono text-xs text-slate-500 hover:text-[#00F0FF] transition-colors inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={12} /> Back to home
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00F0FF] mb-3 block">WHITEPAPER V1.1 — SURGE LAUNCH EDITION</span>
            <h1 className="font-grotesk text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">Qovarix Protocol</h1>
            <p className="font-grotesk text-base text-slate-400 mt-3 max-w-2xl">High-Frequency Binary Options — BlockNexa Labs — February 2026</p>
          </motion.div>
        </div>
      </div>

      {/* Content area */}
      <div className="max-w-5xl mx-auto px-6 py-12 flex gap-10">
        {/* Sidebar TOC */}
        <nav className="hidden lg:block w-56 shrink-0 sticky top-24 self-start max-h-[calc(100vh-8rem)] overflow-y-auto" data-testid="wp-toc">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 mb-4 block">TABLE OF CONTENTS</span>
          {TOC.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`block py-1.5 text-xs font-grotesk transition-colors ${active === item.id ? "text-[#00F0FF]" : "text-slate-500 hover:text-slate-300"}`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Main content */}
        <main className="flex-1 min-w-0" data-testid="wp-content">
          <Sec id="abstract" title="Abstract">
            <p>Qovarix is a high-frequency, AI-powered prediction market protocol deployed on Base Chain. It enables non-custodial, 15–30 second binary options on crypto price movements with sub-second execution and on-chain settlement.</p>
            <p>The protocol implements a hybrid architecture: a low-latency off-chain C++ execution engine for price aggregation, matching, and AI inference, combined with transparent on-chain custody and settlement contracts on Base.</p>
            <p>Qovarix's economic model is centered on a fully revenue-backed buyback-and-burn mechanism. 100% of protocol trading fees are directed toward purchasing and burning the native $QVX token.</p>
            <p>A proprietary AI sentiment engine analyzes real-time order book depth, short-term momentum, and cross-exchange microstructure to provide probabilistic signals to traders. These signals are advisory only.</p>
          </Sec>

          <Sec id="exec" title="1. Executive Summary">
            <Sub title="1.1 Overview">
              <p>Traders can speculate on whether an asset such as BTC or ETH will be higher or lower at the end of a 15–30 second round, with settlement performed in a non-custodial manner on Base Chain.</p>
              <Bullet>High-Frequency Rounds: 15–30 second duration markets.</Bullet>
              <Bullet>Sub-Second Lifecycle: Off-chain computation for matching and AI inference, with on-chain finalization.</Bullet>
              <Bullet>AI-Augmented Decisions: Integrated AI sentiment signals (advisory only).</Bullet>
              <Bullet>Revenue-Driven Tokenomics: 100% of protocol revenue to buyback-and-burn of $QVX.</Bullet>
            </Sub>
            <Sub title="1.2 Key Innovations">
              <p className="font-mono text-xs text-[#B5FF00] uppercase tracking-wider mb-2">Hybrid Architecture</p>
              <Bullet>Off-chain C++ matching and oracle engine delivering millisecond-level latency.</Bullet>
              <Bullet>On-chain settlement on Base Chain, ensuring transparent custody and verifiable outcomes.</Bullet>
              <Bullet>Cryptographic commitment scheme to connect off-chain computation with on-chain finality.</Bullet>
              <p className="font-mono text-xs text-[#B5FF00] uppercase tracking-wider mb-2 mt-4">AI-Powered Edge (Advisory Only)</p>
              <Bullet>Real-time sentiment engine trained on historical tick data and market microstructure.</Bullet>
              <Bullet>Probabilistic "Bullish/Bearish + Confidence" signals derived from order book depth, momentum, volatility regime.</Bullet>
              <p className="font-mono text-xs text-[#B5FF00] uppercase tracking-wider mb-2 mt-4">Sustainable Tokenomics</p>
              <Bullet>100% of trading fees routed to automated buyback-and-burn.</Bullet>
              <Bullet>No ongoing emission-driven yield farming.</Bullet>
              <Bullet>Vesting schedules designed to minimize misaligned incentives.</Bullet>
            </Sub>
            <Sub title="1.3 Target Market">
              <Bullet><strong>High-Frequency Traders:</strong> On-chain and off-chain quants seeking low-latency microstructure.</Bullet>
              <Bullet><strong>Crypto-Native Retail:</strong> Users familiar with perpetuals, attracted by simple up/down mechanics.</Bullet>
              <Bullet><strong>Integrators:</strong> Protocols and market makers that can route flow or build structured products.</Bullet>
            </Sub>
            <Sub title="1.4 Competitive Advantages">
              <div className="glass-panel p-4 overflow-x-auto">
                <table className="w-full font-mono text-xs">
                  <thead><tr className="text-slate-500 border-b border-white/10">{["Feature","Qovarix","Traditional DEXs","Centralized Binary"].map(h=><th key={h} className="text-left py-2 pr-4">{h}</th>)}</tr></thead>
                  <tbody className="text-slate-300">
                    <tr className="border-b border-white/5"><td className="py-2 pr-4 text-white">Round Duration</td><td className="py-2 pr-4 text-[#00F0FF]">15–30s</td><td className="py-2 pr-4">30–60 min</td><td className="py-2 pr-4">1–5 min</td></tr>
                    <tr className="border-b border-white/5"><td className="py-2 pr-4 text-white">Execution</td><td className="py-2 pr-4 text-[#00F0FF]">&lt;100ms</td><td className="py-2 pr-4">12–15s</td><td className="py-2 pr-4">50–200ms</td></tr>
                    <tr className="border-b border-white/5"><td className="py-2 pr-4 text-white">AI Assistance</td><td className="py-2 pr-4 text-[#00F0FF]">Integrated</td><td className="py-2 pr-4">None</td><td className="py-2 pr-4">Premium only</td></tr>
                    <tr className="border-b border-white/5"><td className="py-2 pr-4 text-white">Revenue</td><td className="py-2 pr-4 text-[#00F0FF]">100% burn</td><td className="py-2 pr-4">Treasury</td><td className="py-2 pr-4">Shareholders</td></tr>
                    <tr><td className="py-2 pr-4 text-white">Custody</td><td className="py-2 pr-4 text-[#00F0FF]">Non-custodial</td><td className="py-2 pr-4">Non-custodial</td><td className="py-2 pr-4">Custodial</td></tr>
                  </tbody>
                </table>
              </div>
            </Sub>
          </Sec>

          <Sec id="intro" title="2. Introduction">
            <Sub title="2.1 The Evolution of Prediction Markets">
              <p>Blockchain technology enabled programmatic, non-custodial settlement, global access without intermediaries, and composability with other on-chain primitives.</p>
              <p>Early decentralized prediction markets (Augur, Gnosis) proved the concept but were constrained by high gas costs, long resolution horizons, and complex UX.</p>
            </Sub>
            <Sub title="2.2 The High-Frequency Imperative">
              <Bullet><strong>Speed:</strong> Sub-second perceived latency from click-to-confirmation.</Bullet>
              <Bullet><strong>Transparency:</strong> Deterministic, auditable settlement logic.</Bullet>
              <Bullet><strong>Fairness:</strong> Robust price feeds minimizing information asymmetry.</Bullet>
            </Sub>
            <Sub title="2.3 Origins of Qovarix">
              <p>Initially conceptualized as "TickDeriv" at the LabLab Qubic Hackathon 2025 (2nd place). Rebranded to Qovarix as the project evolved into a robust AI-focused quantitative execution engine on Base Chain.</p>
              <p>Base Chain was selected for low gas costs, Surge ecosystem alignment, and access to the Coinbase/Base liquidity and user base.</p>
            </Sub>
          </Sec>

          <Sec id="market" title="3. Market Analysis">
            <Bullet>Global options market: hundreds of billions USD in notional activity.</Bullet>
            <Bullet>Crypto-native options: multiple billions in annualized volume.</Bullet>
            <Bullet>DeFi prediction markets: growing as regulation tightens around opaque centralized venues.</Bullet>
            <p className="mt-4">Qovarix targets users migrating from centralized binary/betting venues and new DeFi-native strategies requiring short-horizon binary payoffs.</p>
          </Sec>

          <Sec id="problem" title="4. Problem Statement">
            <Sub title="4.1 Latency and UX">
              <Bullet>Ethereum's ~12-second block time makes high-frequency markets infeasible on L1.</Bullet>
              <Bullet>Even L2s with 1–2 second latency struggle to match centralized UX.</Bullet>
            </Sub>
            <Sub title="4.2 Information Asymmetry">
              <Bullet>Retail users lack access to order book analytics, cross-exchange flows, and quantitative models.</Bullet>
              <Bullet>This gap leads to poor outcomes and perception that "the house always wins."</Bullet>
            </Sub>
            <Sub title="4.3 Unsustainable Token Incentives">
              <Bullet>Many DeFi protocols bootstrapped via high emissions, leading to inflation and price collapse.</Bullet>
              <Bullet>Qovarix avoids this by not relying on emissions to attract liquidity.</Bullet>
            </Sub>
          </Sec>

          <Sec id="solution" title="5. The Qovarix Solution">
            <Sub title="5.1 Core Mechanics">
              <p>Non-custodial, pari-mutuel binary options system:</p>
              <Bullet>Users deposit assets (USDC, ETH) into a custody smart contract on Base.</Bullet>
              <Bullet>Each round: select "UP" or "DOWN" relative to start price.</Bullet>
              <Bullet>After 15–30 seconds, round resolves based on oracle aggregation.</Bullet>
              <Bullet>Winning side receives the combined pool, net of 2% protocol fee.</Bullet>
              <div className="glass-panel p-4 mt-4">
                <span className="font-mono text-[9px] text-slate-500 uppercase tracking-wider block mb-2">ILLUSTRATIVE EXAMPLE</span>
                <DataRow label="Round Duration" value="30 seconds" />
                <DataRow label="Start Price (BTC)" value="$97,450.00" />
                <DataRow label="End Price" value="$97,485.50 → UP wins" />
                <DataRow label="Total Pool" value="1,000 USDC" />
                <DataRow label="Protocol Fee (2%)" value="20 USDC → buyback engine" />
                <DataRow label="Net Prize Pool" value="980 USDC shared by UP side" />
              </div>
            </Sub>
            <Sub title="5.2 AI-Assisted Trading (Advisory Only)">
              <p>Before placing a bet, users can optionally consult AI sentiment signals:</p>
              <div className="glass-panel p-4 mt-2">
                <DataRow label="Bullish Probability" value="73%" />
                <DataRow label="Confidence" value="High" />
                <DataRow label="Momentum" value="Strong positive" />
                <DataRow label="Suggested Bias" value="UP" />
                <DataRow label="Risk Level" value="Medium" />
              </div>
              <p className="mt-3 text-sm text-slate-500">AI outputs are advisory only. Users retain full control and responsibility.</p>
            </Sub>
          </Sec>

          <Sec id="arch" title="6. Technical Architecture">
            <Sub title="6.1 Dual-Layer Hybrid Design">
              <p className="font-mono text-xs text-[#00F0FF] uppercase tracking-wider mb-2">Settlement Layer — Base Chain</p>
              <Bullet>Custody and settlement of user funds.</Bullet>
              <Bullet>Enforcement of protocol rules, buyback-and-burn execution, governance logic.</Bullet>
              <Bullet>Solidity ^0.8.20, OpenZeppelin, Chainlink, The Graph.</Bullet>
              <p className="font-mono text-xs text-[#B5FF00] uppercase tracking-wider mb-2 mt-4">Execution Layer — Off-Chain</p>
              <Bullet>Price oracle aggregation, round lifecycle management, AI inference.</Bullet>
              <Bullet>C++20, Boost.Asio, WebSockets, Redis, PostgreSQL.</Bullet>
            </Sub>
            <Sub title="6.2 Security Bridge">
              <Bullet><strong>Cryptographic Commitments:</strong> Pre-commit hash of round result + nonce on-chain, then reveal with verification.</Bullet>
              <Bullet><strong>Relayer Collateral:</strong> Relayers post collateral; malicious submissions result in slashing.</Bullet>
              <Bullet><strong>Multi-Source Price Aggregation:</strong> Binance, Coinbase, Kraken with fallback to on-chain oracles.</Bullet>
            </Sub>
            <Sub title="6.3 Performance Targets">
              <div className="glass-panel p-4">
                <DataRow label="Bet Submission Latency" value="<200ms (target) / ~87ms (bench)" />
                <DataRow label="Round Settlement" value="<1s (target) / ~540ms (bench)" />
                <DataRow label="Price Feed Refresh" value="~100ms (target) / ~95ms (bench)" />
                <DataRow label="AI Inference" value="<100ms (target) / ~43ms (bench)" />
                <DataRow label="Concurrent Users" value="10K+ (target) / ~15K (bench)" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Internal test outcomes; not guarantees in production.</p>
            </Sub>
          </Sec>

          <Sec id="contracts" title="7. Smart Contract Design">
            <Sub title="BettingVault.sol">
              <Bullet>Validates round status, user parameters (stake, direction). Transfers to escrow.</Bullet>
              <Bullet>Accepts relayer proofs, records outcomes, computes net pool and fees.</Bullet>
              <Bullet>Batched claims for gas efficiency. Emergency pause mechanisms.</Bullet>
            </Sub>
            <Sub title="BuybackEngine.sol">
              <Bullet>Aggregates protocol fees. Periodic swaps to $QVX via DEX aggregators.</Bullet>
              <Bullet>Burns to 0x0000...dEaD. Emits transparent events.</Bullet>
            </Sub>
            <Sub title="Gas Optimization">
              <Bullet>Batch operations for settlements and claims.</Bullet>
              <Bullet>Minimal on-chain storage with The Graph indexing.</Bullet>
            </Sub>
          </Sec>

          <Sec id="ai" title="8. AI Sentiment Engine">
            <Sub title="Architecture">
              <Bullet><strong>Inputs:</strong> Order book snapshots, trade tape, volume spikes, volatility indicators, cross-asset correlation.</Bullet>
              <Bullet><strong>Features:</strong> Bid-ask imbalances, price velocity, dynamic volatility bands, whale detection.</Bullet>
              <Bullet><strong>Models:</strong> XGBoost ensembles trained on historical tick data. Periodic retraining.</Bullet>
              <Bullet><strong>Outputs:</strong> Probability scores, confidence levels, directional bias, risk classification.</Bullet>
            </Sub>
            <Sub title="Limitations">
              <p>Backtests suggest non-trivial edge under certain conditions, but are subject to survivorship bias and regime changes. AI outputs are advisory only. Risk management remains the trader's responsibility.</p>
            </Sub>
          </Sec>

          <Sec id="tokenomics" title="9. Tokenomics">
            <div className="glass-panel p-5 mb-6">
              <DataRow label="Name" value="Qovarix" />
              <DataRow label="Ticker" value="$QVX" />
              <DataRow label="Chain" value="Base (ERC-20)" />
              <DataRow label="Total Supply" value="100,000,000 QVX" />
              <DataRow label="Initial Circulating" value="~20,000,000 QVX (20%)" />
              <DataRow label="Decimals" value="18" />
            </div>
            <Sub title="Allocation">
              {[
                { label: "Community & Rewards", pct: "40%", note: "Trading incentives, campaigns, partnerships" },
                { label: "Team & Advisors", pct: "20%", note: "12-month cliff, 24-month linear vesting" },
                { label: "Development Fund", pct: "15%", note: "Audits, R&D, operations" },
                { label: "Liquidity Provision", pct: "15%", note: "DEX liquidity, market making" },
                { label: "Public Sale / Launch", pct: "8%", note: "Fair launch via Surge or partner" },
                { label: "Reserve", pct: "2%", note: "Contingencies" },
              ].map((a) => (
                <div key={a.label} className="flex items-center justify-between py-2 border-b border-white/5">
                  <div><span className="text-white text-sm">{a.label}</span><span className="text-slate-500 text-xs ml-2">— {a.note}</span></div>
                  <span className="font-mono text-sm text-[#00F0FF]">{a.pct}</span>
                </div>
              ))}
            </Sub>
            <Sub title="Utility">
              <Bullet><strong>Governance:</strong> Token-based voting on key parameters and upgrades.</Bullet>
              <Bullet><strong>Economic Alignment:</strong> Direct link between protocol revenue and token demand via burn.</Bullet>
              <Bullet><strong>Access (configurable):</strong> Potential tiered fee discounts or AI feature access.</Bullet>
            </Sub>
          </Sec>

          <Sec id="economic" title="10. Economic Model">
            <Sub title="Revenue & Buyback-and-Burn">
              <p>Primary revenue: 2% trading fee per round. 100% directed to BuybackEngine.</p>
              <div className="glass-panel p-4 mt-3">
                <span className="font-mono text-[9px] text-slate-500 uppercase tracking-wider block mb-2">ILLUSTRATIVE SCENARIO (NOT A FORECAST)</span>
                <DataRow label="Avg Round Pool" value="5,000 USDC" />
                <DataRow label="Rounds/Day" value="2,880" />
                <DataRow label="Daily Volume" value="$14.4M" />
                <DataRow label="Daily Revenue (2%)" value="$288,000" />
                <DataRow label="Daily Burn (@ $0.50/QVX)" value="576,000 QVX" />
              </div>
            </Sub>
          </Sec>

          <Sec id="security" title="11. Security & Audits">
            <Bullet>Engagement with reputable auditors for comprehensive code reviews.</Bullet>
            <Bullet>Multi-signature control, timelocked upgrades, formal verification of critical components.</Bullet>
            <Bullet>Multi-source price feeds, fallback to decentralized oracles, slashing for malicious relayers.</Bullet>
            <Bullet>Hardened infrastructure, secure key management, penetration testing.</Bullet>
            <Bullet>Structured bug bounty program with tiered rewards.</Bullet>
          </Sec>

          <Sec id="governance" title="12. Governance">
            <Bullet><strong>Model:</strong> 1 $QVX = 1 vote. Proposals for parameter changes, treasury allocations, upgrades, emergencies.</Bullet>
            <Bullet><strong>Phase 1 (0–6mo):</strong> Team-led with multi-sig and emergency controls.</Bullet>
            <Bullet><strong>Phase 2 (6–12mo):</strong> Community participation, delegated voting, governance tooling.</Bullet>
            <Bullet><strong>Phase 3 (12mo+):</strong> Progressive decentralization, minimized core team reliance.</Bullet>
          </Sec>

          <Sec id="roadmap" title="13. Roadmap">
            {[
              { phase: "Phase 1 — Foundation (Q1 2026)", items: ["Whitepaper v1.1", "C++ engine prototype", "Base testnet contracts", "AI v1 training", "Audit partner discussions"] },
              { phase: "Phase 2 — Launch (Q2 2026)", items: ["Security audits & bug bounty", "TGE via Surge/partner", "Liquidity provisioning", "Mainnet launch (BTC/ETH)", "AI v1 production"] },
              { phase: "Phase 3 — Expansion (Q3–Q4 2026)", items: ["Additional assets (SOL, AVAX, MATIC)", "AI v2 (deep learning)", "Governance portal", "Referral program", "Cross-protocol integrations", "Non-crypto markets exploration"] },
              { phase: "Phase 4 — Maturity (2027+)", items: ["Programmable trading agents", "White-label solutions", "Institutional partnerships", "Continuous improvements"] },
            ].map((r) => (
              <div key={r.phase} className="mb-6 glass-panel p-5">
                <h3 className="font-grotesk text-base font-semibold text-[#00F0FF] mb-3">{r.phase}</h3>
                {r.items.map((item) => <Bullet key={item}>{item}</Bullet>)}
              </div>
            ))}
          </Sec>

          <Sec id="team" title="14. Team">
            <Sub title="Core Contributors">
              <Bullet><strong>Easin Arafat</strong> — Co-Founder | Blockchain Developer. Smart contract architecture, on-chain settlement.</Bullet>
              <Bullet><strong>MD Samsuzzoha Mondal</strong> — Co-Founder | AI Engineer. Sentiment engine, model training & inference.</Bullet>
            </Sub>
            <Sub title="Planned">
              <Bullet>DeFi strategy advisor with scaling experience.</Bullet>
              <Bullet>Legal and compliance advisor for derivatives and digital assets.</Bullet>
              <Bullet>Frontend engineers, DevOps, community management post-launch.</Bullet>
            </Sub>
          </Sec>

          <Sec id="risk" title="15. Risk Disclosure">
            <Sub title="Technical Risks"><Bullet>Smart contract vulnerabilities, oracle failures, off-chain downtime, software bugs.</Bullet></Sub>
            <Sub title="Market Risks"><Bullet>High volatility, liquidity shortfalls, model underperformance vs. backtests.</Bullet></Sub>
            <Sub title="Regulatory Risks"><Bullet>Changing frameworks, jurisdiction-specific restrictions, token classification changes.</Bullet></Sub>
            <Sub title="Token Risks"><Bullet>Price volatility, concentration of holdings, buyback misconfigurations.</Bullet></Sub>
            <p className="text-sm text-slate-500 mt-4">Users must perform independent risk assessment before engaging with the protocol.</p>
          </Sec>

          <Sec id="legal" title="16. Legal & Compliance">
            <Bullet>Utility-focused token with governance use cases; legal classification varies by jurisdiction.</Bullet>
            <Bullet>Monitoring regulatory developments (MiCA, SEC, FATF); may implement geofencing or KYC/AML if required.</Bullet>
            <Bullet>Certain regions may be restricted based on evolving legal advice.</Bullet>
          </Sec>

          <Sec id="conclusion" title="17. Conclusion">
            <p>Qovarix aims to become a foundational building block for high-frequency risk transfer on Base Chain. By combining high-frequency market structure, AI-driven decision support, and revenue-backed tokenomics, the protocol offers an alternative to opaque centralized binary platforms.</p>
            <p>Alignment with Base and Surge ecosystems, transparent revenue flows, and a deliberate approach to decentralization are central design principles.</p>
          </Sec>

          {/* Legal footer */}
          <div className="mt-16 pt-8 border-t border-white/5">
            <p className="font-grotesk text-xs text-slate-600 leading-relaxed">
              This document is for informational and educational purposes only and does not constitute investment, legal, tax, or financial advice. Participation involves substantial risk including possible loss of all funds. Performance statistics are illustrative and do not guarantee future results.
            </p>
            <div className="mt-6 font-mono text-[9px] text-slate-600 space-y-1">
              <p>Website: qovarix.xyz | GitHub: github.com/blocknexalabs/qovarix</p>
              <p>Twitter: @QovarixHQ | Discord: discord.gg/qovarix | Email: contact@blocknexalabs.com</p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
